import React, { useEffect, useState, useRef } from 'react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { cn } from "@/lib/utils";

interface EventImageLightboxProps {
  isOpen: boolean;
  beforeImageUrl: string | null;
  afterImageUrl: string | null;
  currentImage: 'before' | 'after';
  beforeVersion?: string | null;
  afterVersion?: string | null;
  assetTitle?: string;
  imageType?: 'markup' | 'cropped' | 'original';
  description?: string | null;
  modal?: boolean;
  onClose: () => void;
  onNavigate: (image: 'before' | 'after') => void;
}

export const EventImageLightbox: React.FC<EventImageLightboxProps> = ({
  isOpen,
  beforeImageUrl,
  afterImageUrl,
  currentImage,
  beforeVersion,
  afterVersion,
  assetTitle = 'Image',
  imageType = 'cropped',
  description,
  modal = true,
  onClose,
  onNavigate,
}) => {
  const currentImageUrl = currentImage === 'before' ? beforeImageUrl : afterImageUrl;
  const currentVersion = currentImage === 'before' ? beforeVersion : afterVersion;

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Determine if navigation is available
  const hasBefore = !!beforeImageUrl;
  const hasAfter = !!afterImageUrl;
  const canNavigate = hasBefore && hasAfter;
  const canGoToBefore = currentImage === 'after' && hasBefore;
  const canGoToAfter = currentImage === 'before' && hasAfter;

  // Reset zoom and pan when changing images
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentImage, currentImageUrl]);

  const handlePrevious = () => {
    if (canGoToBefore) {
      onNavigate('before');
    }
  };

  const handleNext = () => {
    if (canGoToAfter) {
      onNavigate('after');
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse handlers for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentImage, canGoToBefore, canGoToAfter, zoom]);

  if (!isOpen || !currentImageUrl) {
    return null;
  }

  // Helper to format version date
  const formatVersion = (version: string | null | undefined): string => {
    if (!version) return 'Unknown';

    const dateMatch = version.match(/(\d{8})/);
    if (!dateMatch) return version;

    const dateStr = dateMatch[1];
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        // Only call onClose when actually closing
        if (!open) {
          onClose();
        }
      }}
      modal={modal}
    >
      <DialogPrimitive.Portal>
        {/* Overlay - click-through when non-modal */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            !modal && "pointer-events-none"
          )}
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-[95vw] h-[95vh] p-0 bg-black/95 border-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onEscapeKeyDown={(e) => {
            // Close the lightbox on escape
            onClose();
            // Prevent escape from bubbling to parent (which might close the Sheet)
            e.preventDefault();
            e.stopPropagation();
          }}
          onInteractOutside={(e) => {
            // When non-modal, don't close on outside clicks (overlay is click-through)
            // Only the close button should close the lightbox
            if (!modal) {
              e.preventDefault();
            }
          }}
        >
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Zoom controls */}
          <div className="absolute top-4 left-4 z-50 flex flex-col gap-2 bg-black/50 rounded-lg p-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={handleZoomIn}
              disabled={zoom >= 5}
              title="Zoom in (+)"
            >
              <ZoomIn className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={handleZoomOut}
              disabled={zoom <= 1}
              title="Zoom out (-)"
            >
              <ZoomOut className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={handleResetZoom}
              disabled={zoom === 1}
              title="Reset zoom (0)"
            >
              <Maximize2 className="h-5 w-5" />
            </Button>
            {zoom > 1 && (
              <div className="text-white text-xs text-center font-medium mt-1">
                {Math.round(zoom * 100)}%
              </div>
            )}
          </div>

          {/* Navigation buttons - only show if we have both images */}
          {canNavigate && (
            <>
              {canGoToBefore && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
              )}

              {canGoToAfter && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              )}
            </>
          )}

          {/* Image */}
          <div
            ref={imageContainerRef}
            className="relative max-w-full max-h-[85vh] flex items-center justify-center p-8 overflow-hidden"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={currentImageUrl}
              alt={`${assetTitle} - ${currentImage}`}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                maxHeight: 'calc(95vh - 100px)',
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                pointerEvents: 'none'
              }}
              draggable={false}
            />
          </div>

          {/* Caption and counter */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
            <div className="flex justify-between items-end">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1">
                  {currentImage === 'before' ? 'Before' : 'After'} ({formatVersion(currentVersion)})
                </h3>
                {description && (
                  <p className="text-sm text-gray-200 italic mb-1 max-w-2xl">
                    {description}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  {assetTitle} - {imageType}
                </p>
              </div>
              {canNavigate && (
                <div className="text-sm text-gray-400 flex-shrink-0">
                  {currentImage === 'before' ? '1' : '2'} / 2
                </div>
              )}
            </div>
          </div>
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
